import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, Calendar, Users,
  FileText, Receipt, DollarSign, Download, CheckCircle2, XCircle, Loader2,
  CreditCard
} from "lucide-react";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";
import { useToast } from "@/hooks/use-toast";
import { usePDFData } from "@/hooks/usePDFData";

interface ChantierBilanProps {
  chantierData: any;
  metrics: any;
  membres: any[];
  affectations: any[];
  devis: any[];
  factures: any[];
  frais: any[];
  dateFinReelle: string;
  paiementTotals?: {
    total_devis: number;
    total_a_encaisser: number;
    total_encaisse: number;
    reste_a_encaisser: number;
    pourcentage_encaisse: number;
  } | null;
}

const ChantierBilan = ({
  chantierData,
  metrics,
  membres,
  affectations,
  devis,
  factures,
  frais,
  dateFinReelle,
  paiementTotals,
}: ChantierBilanProps) => {
  const { toast } = useToast();
  const rentabilityBadge = getRentabilityBadge(metrics?.marge_finale_pct || 0);

  // Hook pour générer le PDF avec le nouveau design
  const { telechargerBilan, isLoading: isPDFLoading } = usePDFData(chantierData.id);
  const devisActif = devis.find(d => d.actif);

  // Calculs
  const budgetInitial = metrics?.budget_ht || 0;
  // Calcul du coût total = MO + fixes + pénalités
  const coutTotal = (metrics?.cout_mo_total || metrics?.cout_main_oeuvre_reel || 0) + (metrics?.couts_fixes_engages || 0) + (metrics?.penalites_retard || 0);
  // Marge = Budget - Coût total (calcul direct, pas de fallback sur metrics)
  const margeRealisee = budgetInitial - coutTotal;

  // Calculer les jours travaillés totaux depuis les affectations (retard inclus)
  // et agréger par membre unique
  const membresMap = new Map<string, { jours: number }>();
  let totalJoursTravailles = 0;

  affectations.forEach((aff: any) => {
    const membreId = aff.membre_equipe_id || aff.id;

    // Calculer les jours depuis jours_details si disponible
    let joursAffectation = 0;
    if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
      joursAffectation = aff.jours_details.reduce((sum: number, jd: any) => sum + (jd.value || 1), 0);
    } else {
      joursAffectation = aff.jours_travailles || 0;
    }

    totalJoursTravailles += joursAffectation;

    // Agréger par membre unique pour le comptage
    if (membresMap.has(membreId)) {
      const existing = membresMap.get(membreId)!;
      existing.jours += joursAffectation;
    } else {
      membresMap.set(membreId, { jours: joursAffectation });
    }
  });

  // Nombre de membres uniques ayant travaillé
  const nbMembresActifs = membresMap.size > 0 ? membresMap.size : membres.length;

  // Durée réelle = total jours travaillés / nombre de membres (moyenne)
  // OU total jours travaillés si on veut l'afficher en jours-homme
  const dureeEstimee = chantierData.duree_estimee_jours || chantierData.duree_estimee || 0;
  const dateDebut = new Date(chantierData.date_debut_prevue || chantierData.date_debut_reelle);
  const dateFin = new Date(dateFinReelle);

  // Durée réelle = jours travaillés moyens par personne (pour comparaison avec durée estimée)
  const dureeReelle = nbMembresActifs > 0
    ? Math.round((totalJoursTravailles / nbMembresActifs) * 10) / 10
    : Math.ceil((dateFin.getTime() - dateDebut.getTime()) / (1000 * 60 * 60 * 24));
  const ecartJours = Math.round((dureeReelle - dureeEstimee) * 10) / 10;

  // Calcul des jours de retard travaillés et leur coût
  // Utiliser les affectations de type "Retard chantier" (source unique de vérité)
  const affectationsRetard = affectations.filter(
    (aff: any) => aff.notes && aff.notes.startsWith('Retard chantier')
  );

  // Dédupliquer les jours de retard par date:period unique
  // (plusieurs membres peuvent travailler le même jour, ça compte pour 1 jour unique)
  const joursRetardUniquesSet = new Set<string>();
  affectationsRetard.forEach((aff: any) => {
    if (aff.jours_details && Array.isArray(aff.jours_details)) {
      aff.jours_details.forEach((jd: any) => {
        joursRetardUniquesSet.add(`${jd.date}:${jd.period || 'FULL'}`);
      });
    }
  });

  // Calculer les jours uniques (FULL=1, AM/PM=0.5)
  let joursRetardTravailles = 0;
  joursRetardUniquesSet.forEach(key => {
    const period = key.split(':')[1];
    joursRetardTravailles += (period === 'FULL') ? 1 : 0.5;
  });

  // Coût de retard = somme des coûts de chaque affectation retard
  let coutRetard = 0;
  affectationsRetard.forEach((aff: any) => {
    const tauxHoraire = aff.taux_horaire_specifique ?? aff.membre?.taux_horaire ?? 0;
    const coef = aff.membre?.coefficient_charges || 1.85;
    const heures = (aff.jours_travailles || 0) * (aff.heures_par_jour || 8);
    coutRetard += heures * tauxHoraire * coef;
  });

  const hasRetard = joursRetardTravailles > 0;

  // Calcul des jours de reprise travaillés et leur coût
  // Utiliser les affectations de type "Reprise chantier" (source unique de vérité)
  const affectationsReprise = affectations.filter(
    (aff: any) => aff.notes === 'Reprise chantier'
  );

  // Dédupliquer les jours de reprise par date:period unique
  const joursRepriseUniquesSet = new Set<string>();
  affectationsReprise.forEach((aff: any) => {
    if (aff.jours_details && Array.isArray(aff.jours_details)) {
      aff.jours_details.forEach((jd: any) => {
        joursRepriseUniquesSet.add(`${jd.date}:${jd.period || 'FULL'}`);
      });
    }
  });

  // Calculer les jours uniques (FULL=1, AM/PM=0.5)
  let joursRepriseTravailles = 0;
  joursRepriseUniquesSet.forEach(key => {
    const period = key.split(':')[1];
    joursRepriseTravailles += (period === 'FULL') ? 1 : 0.5;
  });

  // Coût de reprise = somme des coûts de chaque affectation reprise
  let coutReprise = 0;
  affectationsReprise.forEach((aff: any) => {
    const tauxHoraire = aff.taux_horaire_specifique ?? aff.membre?.taux_horaire ?? 0;
    const coef = aff.membre?.coefficient_charges || 1.85;
    const heures = (aff.jours_travailles || 0) * (aff.heures_par_jour || 8);
    coutReprise += heures * tauxHoraire * coef;
  });

  const hasReprise = joursRepriseTravailles > 0;

  // Fonction pour télécharger le bilan avec le nouveau design
  const handleTelechargerBilan = () => {
    telechargerBilan(chantierData.entreprise?.nom || 'Mon Entreprise', dateFinReelle);
  };

  return (
    <div className="space-y-6">
      {/* Rentabilité finale */}
      <Card className={`${rentabilityBadge.urgency === 'critical' || rentabilityBadge.urgency === 'high' ? 'border-4 border-red-500' : 'border-2'}`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Rentabilité Finale
            </span>
            <Button onClick={handleTelechargerBilan} variant="outline" size="sm" disabled={isPDFLoading}>
              {isPDFLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isPDFLoading ? 'Génération...' : 'Télécharger le bilan'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Badge className={`text-2xl font-bold px-6 py-3 ${rentabilityBadge.bgColor} ${rentabilityBadge.color}`}>
              {rentabilityBadge.emoji} {rentabilityBadge.label} - {(metrics?.marge_finale_pct || 0).toFixed(1)}%
            </Badge>
            <p className="text-sm text-muted-foreground">{rentabilityBadge.message}</p>
          </div>
        </CardContent>
      </Card>

      {/* Synthèse financière */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Synthèse Financière
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Budget initial</p>
              <p className="text-2xl font-bold">{budgetInitial.toLocaleString()} €</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Coût total</p>
              <p className="text-2xl font-bold">{coutTotal.toLocaleString()} €</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Marge réalisée</p>
              <p className={`text-2xl font-bold ${margeRealisee >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {margeRealisee.toLocaleString()} €
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paiement Client */}
      {paiementTotals && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Paiement Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Devis HT</p>
                <p className="text-2xl font-bold">{paiementTotals.total_a_encaisser.toLocaleString('fr-FR')} €</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Encaissé</p>
                <p className={`text-2xl font-bold ${paiementTotals.pourcentage_encaisse >= 100 ? 'text-green-600' : paiementTotals.pourcentage_encaisse > 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                  {paiementTotals.total_encaisse.toLocaleString('fr-FR')} €
                </p>
                <Badge variant="outline" className="mt-1">
                  {paiementTotals.pourcentage_encaisse.toFixed(0)}% encaissé
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Reste à encaisser</p>
                <p className={`text-2xl font-bold ${paiementTotals.reste_a_encaisser > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {paiementTotals.reste_a_encaisser.toLocaleString('fr-FR')} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Planning */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Planning
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {/* Colonne 1 */}
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Période</p>
                <p className="font-semibold">{dateDebut.toLocaleDateString('fr-FR')} → {dateFin.toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Durée réelle</p>
                <p className="font-semibold">{dureeReelle} jours</p>
              </div>
            </div>

            {/* Colonne 2 */}
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Durée estimée</p>
                <p className="font-semibold">{dureeEstimee} jours</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Écart</p>
                <div className="flex items-center gap-2">
                  {ecartJours > 0 ? (
                    <XCircle className="h-4 w-4 text-red-500" />
                  ) : ecartJours < 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : null}
                  <p className={`font-semibold ${ecartJours > 0 ? 'text-red-600' : ecartJours < 0 ? 'text-green-600' : ''}`}>
                    {ecartJours > 0 ? '+' : ''}{ecartJours} jours
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne 3 - Retard travaillé (si existant) */}
            {hasRetard && (
              <div className="space-y-4 border-l border-border pl-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Jours de retard travaillés</p>
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-orange-500" />
                    <p className="font-semibold text-orange-600">
                      {joursRetardTravailles % 1 === 0 ? joursRetardTravailles : joursRetardTravailles.toFixed(1)} jour{joursRetardTravailles > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Coût du retard</p>
                  <p className="font-semibold text-orange-600">{coutRetard.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</p>
                </div>
              </div>
            )}

            {/* Colonne 4 - Reprise travaillée (si existant) */}
            {hasReprise && (
              <div className="space-y-4 border-l border-border pl-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Jours de reprise travaillés</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <p className="font-semibold text-blue-600">
                      {joursRepriseTravailles % 1 === 0 ? joursRepriseTravailles : joursRepriseTravailles.toFixed(1)} jour{joursRepriseTravailles > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Coût de la reprise</p>
                  <p className="font-semibold text-blue-600">{coutReprise.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Équipe */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Équipe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Membres ayant travaillé</p>
              <p className="text-2xl font-bold">{nbMembresActifs}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Coût total main-d'œuvre</p>
              <p className="text-2xl font-bold">{(metrics?.cout_mo_total || metrics?.cout_main_oeuvre_reel || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} €</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Devis</p>
              <p className="font-semibold">{devis.length} version(s)</p>
              {devisActif && (
                <Badge variant="outline" className="mt-1">Actif: {devisActif.version}</Badge>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Factures fournisseurs</p>
              <p className="font-semibold">{factures.length}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Coûts annexes</p>
              <p className="font-semibold">{frais.length}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total coûts fixes</p>
              <p className="font-semibold">{(metrics?.couts_fixes_engages || 0).toLocaleString()} €</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance */}
      <Card className={rentabilityBadge.urgency === 'none' ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : rentabilityBadge.urgency === 'critical' ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {rentabilityBadge.urgency === 'none' ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <TrendingDown className="h-5 w-5 text-orange-600" />}
            Performance Globale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Points forts:</span>
              <div className="flex gap-2">
                {margeRealisee > 0 && <Badge variant="outline" className="bg-green-100 text-green-700">Rentable</Badge>}
                {ecartJours <= 0 && <Badge variant="outline" className="bg-green-100 text-green-700">Délais respectés</Badge>}
                {nbMembresActifs > 0 && <Badge variant="outline" className="bg-blue-100 text-blue-700">Équipe mobilisée</Badge>}
              </div>
            </div>
            {rentabilityBadge.urgency !== 'none' && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Points d'amélioration:</span>
                <div className="flex gap-2">
                  {margeRealisee < 0 && <Badge variant="outline" className="bg-red-100 text-red-700">Déficit</Badge>}
                  {ecartJours > 0 && <Badge variant="outline" className="bg-orange-100 text-orange-700">Retard</Badge>}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChantierBilan;
